import { useCallback, useEffect, useState } from 'react'
import useSendOtpToPhoneNumber from '~repositories/hooks/use-send-authcode'

import { useTimer } from './use-timer'

export const LOCAL_NUMBER = '1'

type GetCodeParams = Readonly<{
  value: string
  options?: { isRepeated?: boolean }
}>

const removeNonNumeric = (value: string) => value.replace(/[^0-9]/g, '')

const useAuthCode = () => {
  const [authCode, setAuthCode] = useState<string>('')
  const [tokenVerifyingCode, setTokenVerifyingCode] = useState<string | null>(null)
  const [isResendClickable, setResendClickable] = useState<boolean>(false)
  const timer = useTimer(0, 30, !isResendClickable)
  const { mutateAsync: sendOtpToPhoneNumber } = useSendOtpToPhoneNumber()

  const handleAuthCode = useCallback((value: string) => {
    return setAuthCode(removeNonNumeric(value))
  }, [])

  const getCode = useCallback(
    async ({ value }: GetCodeParams) => {
      const { token } = await sendOtpToPhoneNumber({
        phone: `${LOCAL_NUMBER}${value}`,
      })

      if (token) {
        setTokenVerifyingCode(token)
      }
    },
    [sendOtpToPhoneNumber]
  )

  const handleSendAgain = useCallback(
    (value: string) => {
      const phoneNumber = removeNonNumeric(value)

      const isCodeSendable = phoneNumber.length === 10

      if (isCodeSendable) {
        setResendClickable(() => false)
        getCode({
          value: phoneNumber,
          options: { isRepeated: true },
        })
      }
    },
    [getCode]
  )

  useEffect(() => {
    if (timer.isCountdownEnd) {
      setResendClickable(() => true)
    }
  }, [timer.isCountdownEnd])

  return {
    authCode,
    tokenVerifyingCode,
    timer,
    isResendClickable,
    handleSendAgain,
    handleAuthCode,
    getCode,
  }
}

export default useAuthCode
